<!--
  r110 · ORDENES CONTRA ACTIVIDAD

  Cruza el CONTADOR de la máquina contra el estado de la ORDEN en ops y solo pinta un renglón
  cuando las dos versiones no cuadran y, después de las anomalías, agrega las OTs abiertas que
  solo conservan paros sin especificar:

    · "Sin OT"               la máquina está dando golpes y no hay ninguna orden en progreso en ops.
    · "Posible OT cumplida"  la orden sigue iniciada, la máquina lleva horas sin un golpe y ya
                             produjo casi todo el plan.
    · "Siguiente OT"         una OT pendiente entra en las próximas 4 h o ya venció su arranque.

  Es un tablero de solo lectura: no cierra ni abre nada. Los renglones se van solos en cuanto se
  inicia la orden o se cierra la que ya cumplió, así que no hay nada que quitar a mano.

  CICLOS, NO PIEZAS: el contador cuenta golpes de la máquina y cada ciclo puede valer N piezas según
  las cavidades del molde. Todo lo que viene del contador se nombra "ciclos"; lo único en piezas es
  el avance contra el plan de la orden (piezasHechas / piezasProgramadas), que sale de ops.

  MISMA FUENTE QUE "ESTADO DE LAS MÁQUINAS" (r61). El back no recalcula nada por su cuenta: quién
  está activa sale de ObtenerMaquinasActivas, el estado y el avance del plan de
  EstadoMaquinasTiempoReal, y los ciclos del mismo núcleo que alimenta la tarjeta de r61 y el badge
  de OPS. Si un número cambia allá, cambia aquí.

  Todo (avisos, KPIs y los umbrales que se citan en las notas) viene de
  GET /ReportesMoldes/OrdenesContraActividad; aquí solo se formatea.
-->
<template>
  <section ref="fsRoot" class="oca-root" :class="{ 'oca-root--fs': isFullscreen }">

    <!-- ═══════════════ HERO ═══════════════ -->
    <header class="oca-hero">
      <!-- El nombre del reporte ya lo pinta el encabezado compartido de Reportes.vue, así que la
           banda arranca con la planta para no repetirlo. -->
      <div class="oca-hero__copy">
        <p class="oca-hero__subtitle">Actividad del contador contra el estado de la orden</p>
      </div>

      <div class="oca-hero__tools">
        <v-select v-model="idArea" :items="areasSelect" :disabled="loading" item-value="idArea" item-text="nombre"
          hide-details dense solo flat class="oca-area" />
        <span class="oca-hero__countdown">próxima consulta en {{ countdownLabel }}</span>
        <button class="oca-hero__btn" :disabled="loading" @click="consultar">
          {{ loading ? 'Consultando…' : 'Actualizar ahora' }}
        </button>
        <button class="oca-hero__btn" :title="fsLabel" @click="extenderPantalla">
          <v-icon size="16" class="oca-hero__btn-icon">{{ fsIcon }}</v-icon>
          <span class="oca-hero__btn-label">{{ fsLabel }}</span>
        </button>
      </div>
    </header>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="oca-progress" />

    <!-- ═══════════════ KPIs ═══════════════ -->
    <div class="oca-kpis">
      <div v-for="kpi in kpis" :key="kpi.etiqueta" class="oca-kpi">
        <div class="oca-kpi__valor" :class="'oca-kpi__valor--' + kpi.tono">
          {{ kpi.valor }}<span v-if="kpi.total !== null" class="oca-kpi__total">/{{ kpi.total }}</span>
        </div>
        <div class="oca-kpi__etiqueta">{{ kpi.etiqueta }}</div>
      </div>
    </div>

    <!-- ═══════════════ AVISOS ═══════════════ -->
    <div class="oca-tabla-wrap">
      <table class="oca-tabla">
        <thead>
          <tr>
            <th class="oca-th oca-th--aviso">AVISO</th>
            <th class="oca-th">MOMENTO</th>
            <th class="oca-th">DETALLE</th>
            <th class="oca-th">ESTADO / ÚLTIMO CICLO</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(aviso, idx) in avisos" :key="'av-' + idx" class="oca-tr"
            :class="{
              'oca-tr--cumplida': aviso.tipo === 'posible-ot-cumplida',
              'oca-tr--sin-plan': aviso.tipo === 'activa-sin-plan',
              'oca-tr--siguiente': aviso.tipo === 'siguiente-ot',
              'oca-tr--paros-pendientes': aviso.tipo === 'ot-con-paros-sin-especificar',
              'oca-tr--siguiente-vencida': aviso.tipo === 'siguiente-ot' && aviso.minutosParaInicio <= 0
            }">
            <td class="oca-td">
              <span class="oca-badge" :class="'oca-badge--' + aviso.tipo">{{ etiquetaTipo(aviso) }}</span>
              <div class="oca-aviso__titulo">{{ aviso.titulo }}</div>
            </td>

            <td class="oca-td">
              <div class="oca-dato">{{ fechaCorta(aviso.inicioProgramado || aviso.desde) }}</div>
              <div class="oca-dato__sub">{{ textoDesde(aviso) }}</div>
            </td>

            <td class="oca-td">
              <template v-if="aviso.tipo === 'siguiente-ot'">
                <div class="oca-dato"><strong>OT {{ aviso.claveSiguiente || 'S/D' }}</strong></div>
                <div class="oca-dato__sub">Molde {{ aviso.moldeSiguiente || 'S/D' }}</div>
              </template>
              <template v-else-if="aviso.tipo === 'sin-ot' || aviso.tipo === 'activa-sin-plan'">
                <div class="oca-dato">
                  <strong>{{ entero(aviso.ciclosSinAsignar) }}</strong> ciclos sin asignar
                </div>
              </template>
              <template v-else-if="aviso.tipo === 'ot-con-paros-sin-especificar'">
                <div class="oca-dato oca-dato--paro-pendiente">
                  <strong>{{ entero(aviso.parosSinEspecificar) }}</strong> {{ textoParos(aviso.parosSinEspecificar) }}
                </div>
                <div class="oca-dato__sub">pendientes de clasificar</div>
              </template>
              <template v-else>
                <div class="oca-dato">
                  <strong>{{ entero(aviso.porcentajePlan) }}%</strong> del plan
                </div>
                <div class="oca-dato__sub">
                  {{ entero(aviso.piezasHechas) }} de {{ entero(aviso.piezasProgramadas) }} piezas
                </div>
              </template>
            </td>

            <td class="oca-td oca-td--fecha">
              <template v-if="aviso.tipo === 'siguiente-ot'">
                <div class="oca-dato">
                  {{ aviso.tieneOtEnCurso ? 'OT actual en curso' : 'Sin OT abierta' }}
                </div>
                <div v-if="aviso.claves.length" class="oca-dato__sub">{{ aviso.claves.join(', ') }}</div>
                <div v-if="aviso.parosSinEspecificar > 0" class="oca-dato__sub oca-dato__sub--paro">
                  {{ entero(aviso.parosSinEspecificar) }} {{ textoParos(aviso.parosSinEspecificar) }} sin especificar
                </div>
              </template>
              <template v-else-if="aviso.tipo === 'ot-con-paros-sin-especificar'">
                <div class="oca-dato">OT en curso</div>
                <div v-if="aviso.claves.length" class="oca-dato__sub">{{ aviso.claves.join(', ') }}</div>
              </template>
              <template v-else>
                <div>{{ fechaLarga(aviso.ultimoCiclo) }}</div>
                <div v-if="aviso.parosSinEspecificar > 0" class="oca-dato__sub oca-dato__sub--paro">
                  OT {{ aviso.claves.join(', ') || 'S/D' }} con {{ entero(aviso.parosSinEspecificar) }}
                  {{ textoParos(aviso.parosSinEspecificar) }} sin especificar
                </div>
              </template>
            </td>
          </tr>

          <tr v-if="!avisos.length">
            <td class="oca-td oca-vacio" colspan="4">
              {{ consultado
                ? 'Nada que revisar: no hay OTs próximas o vencidas, paros por clasificar ni diferencias con la actividad.'
                : 'Consultando actividad…' }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ═══════════════ NOTAS ═══════════════ -->
    <footer class="oca-notas">
      <p>
        <span class="oca-badge oca-badge--siguiente-ot">Siguiente OT</span>
        anuncia la primera OT pendiente desde {{ parametros.horasAvisoSiguienteOt }} h antes de su arranque
        programado. Si llega la hora y no se abre, cambia a <b>OT pendiente</b> y permanece visible aunque otra OT
        siga en curso.
      </p>
      <p>
        <b>Qué vigila.</b>
        <span class="oca-badge oca-badge--sin-ot">Sin OT</span>
        la máquina está dando golpes —al menos uno en los últimos {{ parametros.minutosGolpe }} minutos— y no tiene
        ninguna orden en progreso en ops. Lo que cuente en ese rato no tiene a qué imputarse.
        <span class="oca-badge oca-badge--posible-ot-cumplida">Posible OT cumplida</span>
        la orden sigue iniciada, la máquina lleva más de {{ parametros.horasSinGolpe }} h sin un solo golpe y ya
        produjo al menos el {{ parametros.porcentajePlanMinimo }}% de lo esperado; probablemente terminó y nadie la
        cerró.
      </p>
      <p>
        <span class="oca-badge oca-badge--activa-sin-plan">Activa sin plan</span>
        La maquina sigue registrando actividad, no tiene una OT abierta y ya no tiene otra OT pendiente en el plan de
        produccion vigente.
      </p>
      <p>
        <span class="oca-badge oca-badge--ot-con-paros-sin-especificar">Paros por clasificar</span>
        indica cuántos grupos de paro sin especificar conserva una OT abierta. Si la máquina también tiene una
        anomalía, el dato aparece dentro de ese aviso; si todo lo demás está bien, la OT se muestra al final.
      </p>
      <p>
        <b>Los avisos se van solos.</b>
        En cuanto se inicia la orden siguiente, o se cierra la que ya cumplió, el renglón correspondiente desaparece.
        No hay que quitar nada a mano.
      </p>
      <p>
        <b>Ciclos sin asignar</b>
        es el mismo dato que ves en <b>Estado de las máquinas</b> y que el operador ve en OPS como "piezas sin
        asignar": lo que contó esa máquina en las últimas {{ horasVentana }} h y que ninguna orden recibió. Sale del
        mismo cálculo en el servidor, así que las tres pantallas no pueden diferir.
      </p>
      <p>
        <b>Son ciclos, no piezas.</b>
        El contador cuenta golpes de la máquina, y cada ciclo puede representar varias piezas según las cavidades
        del molde. El único dato en piezas es el avance contra el plan de la orden.
        Consulta al servidor cada {{ minutosPoll }} min, en modo solo lectura.<template v-if="textoUltimaConsulta">
          {{ textoUltimaConsulta }}</template>
      </p>
      <p v-if="error" class="oca-error">
        No se pudo consultar la actividad. Se reintenta en la próxima consulta.
      </p>
    </footer>
  </section>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'

// Cada cuánto se vuelve a preguntar al servidor. Es el mismo ritmo con el que la planta manda su
// actividad, así que consultar más seguido solo repetiría la misma foto.
const POLL_MS = 300000

const AREA_TODAS = -1

export default {
  name: 'OrdenesContraActividad',

  props: {
    active: { type: Boolean, default: true },
  },

  data: () => ({
    loading: false,
    error: false,
    consultado: false,

    areas: [],
    idArea: AREA_TODAS,

    avisos: [],
    resumen: {
      maquinasTotales: 0,
      maquinasProduciendo: 0,
      maquinasActivasSinOt: 0,
      ciclosSinAsignar: 0,
      otsPosiblesCumplidas: 0,
      maquinasActivasSinPlan: 0,
      otsSiguientesPorAbrir: 0,
    },
    parametros: {
      minutosGolpe: 5,
      horasSinGolpe: 3,
      porcentajePlanMinimo: 90,
      minutosVentanaCiclos: 1440,
      horasAvisoSiguienteOt: 4,
    },

    // Momento de la última respuesta correcta: alimenta el "Última: hace N min" de las notas.
    ultimaConsulta: null,
    ahora: Date.now(),

    segundosRestantes: POLL_MS / 1000,
    interval: null,
    intervalTick: null,
    montado: false,

    isFullscreen: false,
  }),

  computed: {
    ...mapState({ nombreEmpresa: 'nombreEmpresa' }),

    minutosPoll() {
      return Math.round(POLL_MS / 60000)
    },

    // La ventana de la columna "ciclos en ops", en horas, para citarla en las notas.
    horasVentana() {
      return Math.round(this.num(this.parametros.minutosVentanaCiclos, 1440) / 60)
    },

    fsLabel() { return this.isFullscreen ? 'Salir de pantalla' : 'Extender pantalla' },
    fsIcon() { return this.isFullscreen ? 'mdi-arrow-collapse-all' : 'mdi-arrow-expand-all' },

    // CAZEL-TLT / Cazel se leen como "Cazel Tultitlán"; cualquier otra empresa se muestra tal cual.
    nombrePlanta() {
      const emp = String(this.nombreEmpresa || '').trim()
      const MAPA = { 'cazel-tlt': 'Cazel Tultitlán', cazel: 'Cazel Tultitlán' }
      return MAPA[emp.toLowerCase()] || emp || '—'
    },

    areasSelect() {
      return [{ idArea: AREA_TODAS, nombre: 'Todas las áreas' }, ...this.areas]
    },

    countdownLabel() {
      const total = Math.max(0, Number(this.segundosRestantes) || 0)
      return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
    },

    kpis() {
      const r = this.resumen
      return [
        {
          valor: this.entero(r.maquinasActivasSinOt),
          total: this.entero(r.maquinasProduciendo),
          etiqueta: 'MÁQUINAS ACTIVAS SIN OT',
          tono: r.maquinasActivasSinOt > 0 ? 'alerta' : 'neutro',
        },
        {
          valor: this.entero(r.maquinasActivasSinPlan),
          total: null,
          etiqueta: 'ACTIVAS SIN PLAN',
          tono: r.maquinasActivasSinPlan > 0 ? 'alerta' : 'neutro',
        },
        {
          valor: this.entero(r.otsSiguientesPorAbrir),
          total: null,
          etiqueta: 'SIGUIENTES OT POR ABRIR',
          tono: r.otsSiguientesPorAbrir > 0 ? 'aviso' : 'neutro',
        },
        // El mismo dato que la tarjeta de Estado de máquinas (r61) y el badge de OPS, nombrado como
        // ciclos: los tres salen del mismo cálculo en el back.
        {
          valor: this.entero(r.ciclosSinAsignar),
          total: null,
          etiqueta: 'CICLOS SIN ASIGNAR',
          tono: r.ciclosSinAsignar > 0 ? 'alerta' : 'neutro',
        },
        {
          valor: this.entero(r.otsPosiblesCumplidas),
          total: null,
          etiqueta: 'OT POSIBLEMENTE CUMPLIDA',
          tono: r.otsPosiblesCumplidas > 0 ? 'aviso' : 'neutro',
        },
        {
          valor: this.entero(r.maquinasProduciendo),
          total: this.entero(r.maquinasTotales),
          etiqueta: 'PRODUCIENDO AHORA',
          tono: 'neutro',
        },
      ]
    },

    // "Última: hace N min". Se recalcula con el tick del contador, no con la respuesta, para que el
    // texto envejezca a la vista en lugar de quedarse congelado en "hace 0 min".
    textoUltimaConsulta() {
      if (!this.ultimaConsulta) return ''
      const minutos = Math.max(0, Math.floor((this.ahora - this.ultimaConsulta) / 60000))
      return minutos === 0 ? 'Última: hace menos de 1 min.' : `Última: hace ${minutos} min.`
    },
  },

  watch: {
    active(n) { n ? this.iniciar() : this.detener() },
    idArea() { this.consultar() },
  },

  mounted() {
    this.montado = true
    if (this.active) this.iniciar()

    // El botón no es la única forma de salir de pantalla completa (Esc, F11): se escucha el evento
    // del navegador para que la etiqueta y el icono no queden al revés.
    const sync = () => {
      this.isFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      )
    }
    document.addEventListener('fullscreenchange', sync)
    document.addEventListener('webkitfullscreenchange', sync)
    document.addEventListener('MSFullscreenChange', sync)
    this._syncFs = sync
  },

  beforeDestroy() {
    this.montado = false
    this.detener()
    if (this._syncFs) {
      document.removeEventListener('fullscreenchange', this._syncFs)
      document.removeEventListener('webkitfullscreenchange', this._syncFs)
      document.removeEventListener('MSFullscreenChange', this._syncFs)
    }
  },

  methods: {
    /* ───────── pantalla completa ───────── */

    async extenderPantalla() {
      const el = this.$refs.fsRoot
      if (!document.fullscreenElement) {
        await el.requestFullscreen?.()
        this.isFullscreen = true
      } else {
        await document.exitFullscreen?.()
        this.isFullscreen = false
      }
    },

    /* ───────── helpers de lectura y formato ───────── */

    // El back tiene doble registro de serializador, así que las llaves pueden llegar en camelCase
    // o en PascalCase.
    val(obj, ...keys) {
      for (const key of keys) {
        if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key]
      }
      return undefined
    },

    num(value, fallback = 0) {
      const n = Number(value)
      return Number.isFinite(n) ? n : fallback
    },

    entero(value) {
      return Math.round(this.num(value)).toLocaleString('es-MX')
    },

    parseFecha(valor) {
      if (!valor) return null
      const d = new Date(valor)
      return Number.isNaN(d.getTime()) ? null : d
    },

    dosDigitos(n) {
      return String(n).padStart(2, '0')
    },

    // "03/08 09:24"
    fechaCorta(valor) {
      const d = this.parseFecha(valor)
      if (!d) return '—'
      return `${this.dosDigitos(d.getDate())}/${this.dosDigitos(d.getMonth() + 1)} ` +
        `${this.dosDigitos(d.getHours())}:${this.dosDigitos(d.getMinutes())}`
    },

    // "03/08 16:05:15"
    fechaLarga(valor) {
      const d = this.parseFecha(valor)
      if (!d) return '—'
      return `${this.fechaCorta(valor)}:${this.dosDigitos(d.getSeconds())}`
    },

    etiquetaTipo(aviso) {
      const tipo = typeof aviso === 'string' ? aviso : aviso.tipo
      if (tipo === 'siguiente-ot') {
        return this.num(aviso.minutosParaInicio, 1) <= 0 ? 'OT pendiente' : 'Siguiente OT'
      }
      if (tipo === 'posible-ot-cumplida') return 'Posible OT cumplida'
      if (tipo === 'activa-sin-plan') return 'Activa sin plan'
      if (tipo === 'ot-con-paros-sin-especificar') return 'Paros por clasificar'
      return 'Sin OT'
    },

    textoParos(cantidad) {
      return this.num(cantidad) === 1 ? 'paro' : 'paros'
    },

    // Debajo de la fecha: cuánto lleva contando sin orden, o cuánto lleva parada con la orden
    // abierta. Son dos relojes distintos, así que cada tipo de aviso trae el suyo.
    textoDesde(aviso) {
      if (aviso.tipo === 'siguiente-ot') {
        const minutos = Math.trunc(this.num(aviso.minutosParaInicio))
        if (minutos === 0) return 'debe iniciar ahora'
        const total = Math.abs(minutos)
        const horas = Math.floor(total / 60)
        const resto = total % 60
        const partes = []
        if (horas > 0) partes.push(`${horas} h`)
        if (resto > 0 || !partes.length) partes.push(`${resto} min`)
        return minutos > 0
          ? `inicia en ${partes.join(' ')}`
          : `debió iniciar hace ${partes.join(' ')}`
      }
      if (aviso.tipo === 'sin-ot' || aviso.tipo === 'activa-sin-plan') {
        if (aviso.minutosProduciendo === null) return 'sin bloque de actividad registrado'
        return `produciendo desde hace ${this.entero(aviso.minutosProduciendo)} min`
      }
      if (aviso.tipo === 'ot-con-paros-sin-especificar') return 'primer paro pendiente'
      if (aviso.horasSinGolpe === null) return 'sin golpes registrados'
      return `parada desde hace ${this.num(aviso.horasSinGolpe).toFixed(1)} h`
    },

    /* ───────── carga de datos ───────── */

    puedeConsultar() {
      return this.montado && this.active
    },

    async getAreas() {
      try {
        const { data } = await axios.get('/Areas')
        this.areas = (data || []).filter(a => a.idArea > 1)
      } catch (e) {
        console.log(e)
      }
    },

    adaptAviso(item) {
      return {
        tipo: this.val(item, 'tipo', 'Tipo') || 'sin-ot',
        idMaquinaIns: this.val(item, 'idMaquinaIns', 'IdMaquinaIns') ?? null,
        maquina: this.val(item, 'maquina', 'Maquina') || '',
        area: this.val(item, 'area', 'Area') || '',
        titulo: this.val(item, 'titulo', 'Titulo') || '',
        desde: this.val(item, 'desde', 'Desde') || null,
        // null explícito: "no hay reloj que mostrar" no es lo mismo que "0 minutos".
        minutosProduciendo: this.val(item, 'minutosProduciendo', 'MinutosProduciendo') ?? null,
        horasSinGolpe: this.val(item, 'horasSinGolpe', 'HorasSinGolpe') ?? null,
        ultimoCiclo: this.val(item, 'ultimoCiclo', 'UltimoCiclo') || null,
        claves: this.val(item, 'claves', 'Claves') || [],
        parosSinEspecificar: this.num(this.val(item, 'parosSinEspecificar', 'ParosSinEspecificar')),
        // Ciclos del contador; piezasHechas/piezasProgramadas sí son piezas (avance del plan).
        ciclosSinAsignar: this.num(this.val(item, 'ciclosSinAsignar', 'CiclosSinAsignar')),
        piezasHechas: this.num(this.val(item, 'piezasHechas', 'PiezasHechas')),
        piezasProgramadas: this.num(this.val(item, 'piezasProgramadas', 'PiezasProgramadas')),
        porcentajePlan: this.num(this.val(item, 'porcentajePlan', 'PorcentajePlan')),
        idTrabajoOrdenSiguiente: this.val(item, 'idTrabajoOrdenSiguiente', 'IdTrabajoOrdenSiguiente') ?? null,
        claveSiguiente: this.val(item, 'claveSiguiente', 'ClaveSiguiente') || '',
        moldeSiguiente: this.val(item, 'moldeSiguiente', 'MoldeSiguiente') || '',
        inicioProgramado: this.val(item, 'inicioProgramado', 'InicioProgramado') || null,
        minutosParaInicio: this.val(item, 'minutosParaInicio', 'MinutosParaInicio') ?? null,
        tieneOtEnCurso: !!this.val(item, 'tieneOtEnCurso', 'TieneOtEnCurso'),
      }
    },

    async consultar() {
      if (this.loading || !this.puedeConsultar()) return
      this.loading = true
      try {
        const params = new URLSearchParams()
        if (this.idArea > 1) params.append('idsArea', this.idArea)
        const query = params.toString()
        const { data } = await axios.get(`/ReportesMoldes/OrdenesContraActividad${query ? `?${query}` : ''}`)

        const resumen = this.val(data, 'resumen', 'Resumen') || {}
        const parametros = this.val(data, 'parametros', 'Parametros') || {}

        this.resumen = {
          maquinasTotales: this.num(this.val(resumen, 'maquinasTotales', 'MaquinasTotales')),
          maquinasProduciendo: this.num(this.val(resumen, 'maquinasProduciendo', 'MaquinasProduciendo')),
          maquinasActivasSinOt: this.num(this.val(resumen, 'maquinasActivasSinOt', 'MaquinasActivasSinOt')),
          maquinasActivasSinPlan: this.num(this.val(resumen, 'maquinasActivasSinPlan', 'MaquinasActivasSinPlan')),
          ciclosSinAsignar: this.num(this.val(resumen, 'ciclosSinAsignar', 'CiclosSinAsignar')),
          otsPosiblesCumplidas: this.num(this.val(resumen, 'otsPosiblesCumplidas', 'OtsPosiblesCumplidas')),
          otsSiguientesPorAbrir: this.num(this.val(resumen, 'otsSiguientesPorAbrir', 'OtsSiguientesPorAbrir')),
        }

        // Los umbrales se citan en las notas al pie: se toman de la respuesta para que el texto
        // diga siempre con qué reglas se calculó, no un número escrito a mano aquí.
        this.parametros = {
          minutosGolpe: this.num(this.val(parametros, 'minutosGolpe', 'MinutosGolpe'), 5),
          horasSinGolpe: this.num(this.val(parametros, 'horasSinGolpe', 'HorasSinGolpe'), 3),
          porcentajePlanMinimo: this.num(this.val(parametros, 'porcentajePlanMinimo', 'PorcentajePlanMinimo'), 90),
          minutosVentanaCiclos: this.num(this.val(parametros, 'minutosVentanaCiclos', 'MinutosVentanaCiclos'), 1440),
          horasAvisoSiguienteOt: this.num(
            this.val(parametros, 'horasAvisoSiguienteOt', 'HorasAvisoSiguienteOt'),
            4
          ),
        }

        const avisos = this.val(data, 'avisos', 'Avisos') || []
        this.avisos = (Array.isArray(avisos) ? avisos : []).map(a => this.adaptAviso(a))

        this.ultimaConsulta = Date.now()
        this.error = false
      } catch (e) {
        console.log(e)
        this.error = true
      } finally {
        this.loading = false
        this.consultado = true
        this.reiniciarCuenta()
      }
    },

    /* ───────── ciclo ───────── */

    reiniciarCuenta() {
      this.segundosRestantes = Math.round(POLL_MS / 1000)
    },

    iniciarTick() {
      if (this.intervalTick) return
      this.intervalTick = setInterval(() => {
        this.segundosRestantes = Math.max(0, this.segundosRestantes - 1)
        this.ahora = Date.now()
      }, 1000)
    },

    async iniciar() {
      if (!this.puedeConsultar()) return
      this.getAreas()
      await this.consultar()
      if (!this.puedeConsultar() || this.interval) return
      this.interval = setInterval(() => this.consultar(), POLL_MS)
      this.iniciarTick()
    },

    detener() {
      if (this.interval) { clearInterval(this.interval); this.interval = null }
      if (this.intervalTick) { clearInterval(this.intervalTick); this.intervalTick = null }
    },
  },
}
</script>

<style scoped>
.oca-root {
  --oca-verde: #1a7a63;
  --oca-verde-oscuro: #14634f;
  --oca-alerta: #b32a2a;
  --oca-aviso: #9a7411;
  --oca-texto: #1b2430;
  --oca-texto-suave: #5a6476;
  --oca-borde: rgba(0, 0, 0, 0.08);

  font-family: 'Space Grotesk', 'Roboto', sans-serif;
  color: var(--oca-texto);
  background: #ffffff;
}

/* ═══════════════ HERO ═══════════════ */
.oca-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 18px 24px;
  background: var(--oca-verde);
  color: #ffffff;
}

.oca-hero__title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.2;
}

.oca-hero__subtitle {
  margin: 2px 0 0;
  font-size: 13px;
  font-weight: 400;
  opacity: 0.85;
}

.oca-hero__tools {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.oca-hero__countdown {
  font-size: 12px;
  opacity: 0.9;
  white-space: nowrap;
}

.oca-hero__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border: none;
  border-radius: 6px;
  background: var(--oca-verde-oscuro);
  color: #ffffff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.18s;
  outline: none;
  white-space: nowrap;
}

.oca-hero__btn:hover:not(:disabled) {
  filter: brightness(1.18);
}

.oca-hero__btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.oca-hero__btn-icon {
  color: #ffffff !important;
}

/* El select va sobre el verde del hero, así que se aclara el fondo en vez de usar el blanco de
   Vuetify (que rompería la banda). */
.oca-area {
  width: 190px;
  flex: 0 0 auto;
}

.oca-area>>>.v-input__slot {
  background: rgba(255, 255, 255, 0.16) !important;
  box-shadow: none !important;
  min-height: 34px !important;
  padding: 0 10px !important;
}

.oca-area>>>.v-select__selection,
.oca-area>>>.v-icon {
  color: #ffffff !important;
  font-size: 13px;
}

.oca-progress {
  margin: 0;
}

/* ═══════════════ KPIs ═══════════════ */
.oca-kpis {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  padding: 20px 24px;
  background: #f7f8fa;
}

.oca-kpi {
  padding: 16px 18px;
  border: 1px solid var(--oca-borde);
  border-radius: 8px;
  background: #ffffff;
}

.oca-kpi__valor {
  font-size: 30px;
  font-weight: 700;
  line-height: 1.1;
}

.oca-kpi__valor--alerta {
  color: var(--oca-alerta);
}

.oca-kpi__valor--aviso {
  color: var(--oca-aviso);
}

.oca-kpi__valor--neutro {
  color: var(--oca-texto);
}

.oca-kpi__total {
  font-size: 18px;
  font-weight: 600;
  color: var(--oca-texto-suave);
}

.oca-kpi__etiqueta {
  margin-top: 6px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: var(--oca-texto-suave);
}

/* ═══════════════ TABLA ═══════════════ */
.oca-tabla-wrap {
  padding: 0 24px;
  overflow-x: auto;
}

.oca-tabla {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
  border: 1px solid var(--oca-borde);
  border-radius: 8px;
  overflow: hidden;
}

.oca-th {
  padding: 12px 16px;
  background: var(--oca-verde);
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-align: left;
  white-space: nowrap;
}

.oca-th--aviso {
  width: 55%;
}

.oca-tr {
  border-top: 1px solid var(--oca-borde);
}

.oca-tr--cumplida {
  background: #fdf8ec;
}

.oca-tr--sin-plan {
  background: #eef5ff;
}

.oca-tr--siguiente {
  background: #f2f7ff;
}

.oca-tr--paros-pendientes {
  background: #fffaf0;
}

.oca-tr--siguiente-vencida {
  background: #fff1ed;
}

.oca-td {
  padding: 12px 16px;
  font-size: 13px;
  vertical-align: middle;
}

.oca-td--fecha {
  color: var(--oca-texto-suave);
  white-space: nowrap;
}

.oca-aviso__titulo {
  margin-top: 6px;
  font-size: 14px;
  font-weight: 700;
}

.oca-dato {
  font-size: 13px;
}

.oca-dato__sub {
  margin-top: 2px;
  font-size: 12px;
  color: var(--oca-texto-suave);
}

.oca-dato--paro-pendiente,
.oca-dato__sub--paro {
  color: var(--oca-aviso);
  font-weight: 600;
}

.oca-badge {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.oca-badge--sin-ot {
  background: #fdeaee;
  color: #b32a5c;
}

.oca-badge--posible-ot-cumplida {
  background: #fbf1d8;
  color: #8a6508;
}

.oca-badge--activa-sin-plan {
  background: #dcecff;
  color: #1257c7;
}

.oca-badge--siguiente-ot {
  background: #e2edff;
  color: #1858b8;
}

.oca-badge--ot-con-paros-sin-especificar {
  background: #fff0cc;
  color: #815d00;
}

.oca-tr--siguiente-vencida .oca-badge--siguiente-ot {
  background: #ffe0d8;
  color: #b4381f;
}

.oca-vacio {
  padding: 28px 16px;
  color: var(--oca-texto-suave);
  text-align: center;
}

/* ═══════════════ NOTAS ═══════════════ */
.oca-notas {
  padding: 18px 24px 28px;
  font-size: 12.5px;
  line-height: 1.65;
  color: var(--oca-texto-suave);
}

.oca-notas p {
  margin: 0 0 4px;
}

.oca-notas b {
  color: var(--oca-texto);
}

.oca-error {
  margin-top: 8px !important;
  color: var(--oca-alerta);
  font-weight: 600;
}

/* ═══════════════ PANTALLA COMPLETA ═══════════════ */
/* El navegador pinta el elemento en fullscreen sobre negro, así que el fondo se fuerza aquí. El
   scroll vive dentro de la sección porque en fullscreen ya no hay página que lo lleve. */
.oca-root:fullscreen,
.oca-root:-webkit-full-screen {
  background: #ffffff;
  height: 100vh;
  overflow-y: auto;
}

/* La banda se queda pegada arriba: en un tablero de pared el botón de salir y la cuenta regresiva
   tienen que seguir a la vista aunque la lista de avisos sea larga. */
.oca-root--fs .oca-hero {
  position: sticky;
  top: 0;
  z-index: 2;
}

.oca-root--fs .oca-kpi__valor {
  font-size: 38px;
}

.oca-root--fs .oca-td,
.oca-root--fs .oca-aviso__titulo {
  font-size: 15px;
}

/* ═══════════════ RESPONSIVE ═══════════════ */
@media (max-width: 600px) {
  .oca-hero {
    padding: 14px 14px;
  }

  /* En móvil el botón se queda solo con el icono para que la barra no se parta en tres renglones. */
  .oca-hero__btn-label {
    display: none;
  }

  .oca-hero__title {
    font-size: 18px;
  }

  .oca-kpis {
    padding: 14px;
    gap: 12px;
  }

  .oca-tabla-wrap {
    padding: 0 14px;
  }

  .oca-notas {
    padding: 14px 14px 22px;
  }

  .oca-area {
    width: 100%;
  }
}
</style>
